import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const CompanyCountPage = ({ data, onUpdate }) => {
  const [companyCount, setCompanyCount] = useState(data.companyCount || "");
  const [selectedOption, setSelectedOption] = useState(data.selectedOption || "");
  const [fieldErrors, setFieldErrors] = useState({
    companyCount: ''
  });
  
  // Ref for the input field to focus it when "More" is selected
  const inputRef = useRef(null);

  // Update parent component when data changes
  useEffect(() => {
    onUpdate({
      companyCount,
      selectedOption
    });
  }, [companyCount, selectedOption]);

  // Handle input change
  const handleCompanyCountChange = (e) => {
    const value = e.target.value;
    
    // Only allow numbers and empty string
    if (value === '' || /^\d+$/.test(value)) {
      setCompanyCount(value);
      
      // Clear validation error when user makes a change
      setFieldErrors(prev => ({
        ...prev,
        companyCount: ''
      }));
    }
  };

  // Handle quick selection buttons
  const handleQuickSelect = (option) => {
    setSelectedOption(option);
    
    if (option === 'more') {
      // Clear company count when switching to "More"
      setCompanyCount('');
      
      // Focus the input field after a short delay to ensure it's rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      // Set company count for specific numbers
      setCompanyCount(option);
    }
    
    setFieldErrors(prev => ({
      ...prev,
      companyCount: ''
    }));
  };

  // Validate input
  const validateInput = (value) => {
    if (!value || value.trim() === '') {
      return 'Please enter the number of companies';
    }
    
    const num = parseInt(value);
    if (isNaN(num) || num < 1) {
      return 'Please enter a number greater than 0';
    }
    
    if (num > 100) {
      return 'Please enter a number less than 100';
    }
    
    return '';
  };

  // Handle blur validation
  const handleBlur = () => {
    const error = validateInput(companyCount);
    setFieldErrors(prev => ({
      ...prev,
      companyCount: error
    }));
  };

  return (
    <div className="wizard-page company-count-page">
      <div className="page-header">
        <h2>How many companies are you planning to use with CashManager?</h2>
        <p>You can always add more companies later.</p>
      </div>

      <div className="form-content" id="company-count-form">
        <div className="quick-select-section">
          <div className="quick-select-buttons">
            <button
              type="button"
              className={`quick-select-btn ${selectedOption === '1' ? 'selected' : ''}`}
              onClick={() => handleQuickSelect('1')}
            >
              1
            </button>
            <button
              type="button"
              className={`quick-select-btn ${selectedOption === '2' ? 'selected' : ''}`}
              onClick={() => handleQuickSelect('2')}
            >
              2
            </button>
            <button
              type="button"
              className={`quick-select-btn ${selectedOption === '3' ? 'selected' : ''}`}
              onClick={() => handleQuickSelect('3')}
            >
              3
            </button>
            <button
              type="button"
              className={`quick-select-btn ${selectedOption === '4' ? 'selected' : ''}`}
              onClick={() => handleQuickSelect('4')}
            >
              4
            </button>
            <button
              type="button"
              className={`quick-select-btn ${selectedOption === 'more' ? 'selected' : ''}`}
              onClick={() => handleQuickSelect('more')}
            >
              ...
            </button>
          </div>
        </div>

        {selectedOption === 'more' && (
          <div className="form-group">
            
            <div className="input-container">
              <input
                ref={inputRef}
                type="text"
                id="companyCount"
                name="companyCount"
                value={companyCount}
                onChange={handleCompanyCountChange}
                onBlur={handleBlur}
                placeholder="Enter number"
                className={`form-input ${fieldErrors.companyCount ? 'error' : ''}`}
                required
              />
              <span className="input-suffix">companies</span>
            </div>
            
            {fieldErrors.companyCount && (
              <span className="field-error">{fieldErrors.companyCount}</span>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

CompanyCountPage.propTypes = {
  data: PropTypes.shape({
    companyCount: PropTypes.string,
    selectedOption: PropTypes.string
  }).isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default CompanyCountPage;
